import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Workstation, Procedure, Customer, AlarmTarget, WorkstationServiceStatus } from '@visionqc/shared';
import { PROCGUARD_TEMPLATES, findTemplate } from '@visionqc/shared';
import { useIsAdmin } from '../api/auth';
import { api } from '../api/client';
import { procguardApi, listSafetyCameras, createSafetyCamera, type SafetyCameraLite, type ProcguardModel } from '../api/procguard';
import { ServiceStatusPill } from '../components/ServiceStatusPill';

/**
 * procguard authoring — workstations, their cameras, and the procedures on them.
 * The step editor (ROIs, components) lives on its own route. Kept deliberately
 * plain and legible: this is set up once by an engineer, not watched all day.
 */
export function ProcGuardView() {
  const isAdmin = useIsAdmin();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [cameras, setCameras] = useState<SafetyCameraLite[]>([]);
  const [workstations, setWorkstations] = useState<Workstation[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const reloadCameras = useCallback(() => {
    return listSafetyCameras().then(setCameras).catch(() => undefined);
  }, []);
  useEffect(() => {
    api.listCustomers().then((cs) => {
      setCustomers(cs);
      setCustomerId((id) => id ?? cs[0]?.id ?? null);
    }).catch(() => undefined);
    reloadCameras();
  }, [reloadCameras]);

  const load = useCallback(() => {
    if (!customerId) return;
    procguardApi.listWorkstations(customerId).then(setWorkstations).catch((e) => setErr(String(e.message)));
  }, [customerId]);
  useEffect(() => load(), [load]);

  async function seedDemo() {
    if (!customerId) return;
    try {
      // A ready-to-explore demo: a workstation with a published SOP from the
      // kitting template. The camera slot is left empty (nothing to fabricate) —
      // the status pill will say "No camera" until one is attached.
      const t = findTemplate('kit-3-part');
      const ws = await procguardApi.createWorkstation({
        customerId,
        name: 'Demo — Kit bench',
        site: 'Demo',
        line: '1',
        cameras: [],
        enabled: false,
      });
      const proc = await procguardApi.importProcedure(ws.id, t!.doc);
      await procguardApi.updateProcedure(proc.id, { status: 'published' });
      load();
    } catch (e) { setErr((e as Error).message); }
  }

  if (!isAdmin) return <div className="panel muted">procguard authoring requires the ADMIN role.</div>;

  const myCameras = cameras.filter((c) => c.customerId === customerId);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Jakatori — process verification</h1>
        <div className="row" style={{ gap: 8 }}>
          {customerId && <button className="btn sm" onClick={() => void seedDemo()} title="Create a demo workstation with a published SOP from a template">+ Demo workstation</button>}
          <Link className="btn sm" to="/procguard/dashboard">Analytics</Link>
          <Link className="btn sm" to="/procguard/parts">Parts master</Link>
          <Link className="btn sm" to="/procguard/cycles">Cycles</Link>
        </div>
      </div>
      <p className="muted" style={{ marginTop: -6, maxWidth: 720 }}>
        An SOP is the ordered sequence of tasks a worker performs at a workstation. Set up
        a workstation, point it at its cameras, then author the steps.
      </p>

      {err && <div className="panel" style={{ color: 'var(--bad)' }}>{err}</div>}

      <div className="panel" style={{ marginBottom: 16 }}>
        <label>Customer</label>
        <select value={customerId ?? ''} onChange={(e) => setCustomerId(Number(e.target.value))}>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {customerId && (
        <NewWorkstation customerId={customerId} cameras={myCameras} onDone={load} onError={setErr} reloadCameras={reloadCameras} />
      )}

      {workstations.length === 0 ? (
        <div className="panel muted">No workstations yet for this customer.</div>
      ) : (
        workstations.map((ws) => (
          <WorkstationCard key={ws.id} ws={ws} cameras={myCameras} onChanged={load} onError={setErr} reloadCameras={reloadCameras} />
        ))
      )}
    </div>
  );
}

function CameraChooser({
  cameras,
  value,
  onChange,
  customerId,
  onCreated,
  onError,
}: {
  cameras: SafetyCameraLite[];
  value: number[];
  onChange: (ids: number[]) => void;
  /** When set, an inline "+ Add camera" creates one dedicated to this customer. */
  customerId?: number;
  onCreated?: (id: number) => void;
  onError?: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    if (!customerId || name.trim().length < 1) return;
    setBusy(true);
    try {
      const { id } = await createSafetyCamera({ name: name.trim(), customerId, source: source.trim() || 'upload' });
      setAdding(false); setName(''); setSource('');
      onCreated?.(id);
    } catch (e) { onError?.((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {cameras.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No cameras for this customer yet.</span>}
        {cameras.map((c) => {
          const on = value.includes(c.id);
          return (
            <button key={c.id} className={`chip ${on ? 'selected' : ''}`} type="button"
              onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}>
              {on && (value[0] === c.id ? '★ ' : '✓ ')}{c.name}
            </button>
          );
        })}
        {customerId != null && !adding && (
          <button className="btn sm" type="button" onClick={() => setAdding(true)}>＋ Add camera</button>
        )}
      </div>
      {adding && (
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label style={{ fontSize: 12 }}>Camera name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bench 1 — top" style={{ minWidth: 160 }} /></div>
          <div style={{ flex: 1, minWidth: 200 }}><label style={{ fontSize: 12 }}>RTSP/HTTP source <span className="muted">(optional — set later under Safety)</span></label><input value={source} onChange={(e) => setSource(e.target.value)} placeholder="rtsp://user:pass@ip:554/…" style={{ width: '100%' }} /></div>
          <button className="btn sm primary" disabled={busy || name.trim().length < 1} onClick={() => void create()}>Add</button>
          <button className="btn sm" onClick={() => { setAdding(false); setName(''); setSource(''); }}>Cancel</button>
        </div>
      )}
      {customerId != null && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          New cameras are dedicated to this customer. Fine-tune the live connection (autodetect, credentials) under Safety → Zones.
        </div>
      )}
    </div>
  );
}

function NewWorkstation({
  customerId,
  cameras,
  onDone,
  onError,
  reloadCameras,
}: {
  customerId: number;
  cameras: SafetyCameraLite[];
  onDone: () => void;
  onError: (m: string) => void;
  reloadCameras: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [site, setSite] = useState('');
  const [line, setLine] = useState('');
  const [camIds, setCamIds] = useState<number[]>([]);

  async function create() {
    try {
      await procguardApi.createWorkstation({
        customerId,
        name: name.trim(),
        site,
        line,
        cameras: camIds.map((id, i) => ({ cameraId: id, label: '', isPrimary: i === 0 })),
        enabled: false,
      });
      setOpen(false); setName(''); setSite(''); setLine(''); setCamIds([]);
      onDone();
    } catch (e) {
      onError((e as Error).message);
    }
  }

  if (!open) {
    return (
      <button className="btn sm" style={{ marginBottom: 16 }} onClick={() => setOpen(true)}>+ New workstation</button>
    );
  }
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <strong>New workstation</strong>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginTop: 10 }}>
        <div><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kitting bench 1" /></div>
        <div><label>Site</label><input value={site} onChange={(e) => setSite(e.target.value)} placeholder="Doussard" /></div>
        <div><label>Line</label><input value={line} onChange={(e) => setLine(e.target.value)} placeholder="Line 3" /></div>
      </div>
      <label style={{ marginTop: 10 }}>Cameras <span className="muted">(first is primary ★)</span></label>
      <CameraChooser cameras={cameras} value={camIds} onChange={setCamIds} customerId={customerId} onError={onError}
        onCreated={(cid) => { setCamIds((v) => (v.includes(cid) ? v : [...v, cid])); void reloadCameras(); }} />
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn sm primary" disabled={name.trim().length < 1} onClick={() => void create()}>Create</button>
        <button className="btn sm" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function WorkstationCard({
  ws,
  cameras,
  onChanged,
  onError,
  reloadCameras,
}: {
  ws: Workstation;
  cameras: SafetyCameraLite[];
  onChanged: () => void;
  onError: (m: string) => void;
  reloadCameras: () => Promise<unknown>;
}) {
  const nav = useNavigate();
  const [procs, setProcs] = useState<Procedure[]>([]);
  const [editCams, setEditCams] = useState(false);
  const [camIds, setCamIds] = useState<number[]>(ws.cameras.map((c) => c.cameraId));
  const [showAlarm, setShowAlarm] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [status, setStatus] = useState<WorkstationServiceStatus | null>(null);
  // null = the create input is closed; a string = open with that draft name.
  const [newSop, setNewSop] = useState<string | null>(null);

  const loadProcs = useCallback(() => {
    procguardApi.listProcedures(ws.id).then(setProcs).catch(() => undefined);
  }, [ws.id]);
  useEffect(() => loadProcs(), [loadProcs]);

  // Poll the live service state while the card is on screen, so "Enabled" is
  // distinguished from "actually running" (and from "on but missing a camera").
  const loadStatus = useCallback(() => {
    procguardApi.getStatus(ws.id).then(setStatus).catch(() => undefined);
  }, [ws.id]);
  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [loadStatus]);

  async function toggleEnabled() {
    try {
      await procguardApi.updateWorkstation(ws.id, { enabled: !ws.enabled });
      onChanged();
      loadStatus();
    } catch (e) { onError((e as Error).message); }
  }
  async function saveCams() {
    try {
      await procguardApi.updateWorkstation(ws.id, { cameras: camIds.map((id, i) => ({ cameraId: id, label: '', isPrimary: i === 0 })) });
      setEditCams(false); onChanged();
    } catch (e) { onError((e as Error).message); }
  }
  async function createSop(name: string) {
    if (!name.trim()) return;
    try {
      const p = await procguardApi.createProcedure({ workstationId: ws.id, name: name.trim() });
      nav(`/procguard/procedure/${p.id}`);
    } catch (e) { onError((e as Error).message); }
  }
  async function removeWs() {
    if (!confirm(`Delete workstation "${ws.name}" and its procedures?`)) return;
    try { await procguardApi.deleteWorkstation(ws.id); onChanged(); } catch (e) { onError((e as Error).message); }
  }
  async function fromTemplate(key: string) {
    const t = findTemplate(key);
    if (!t) return;
    try {
      const p = await procguardApi.importProcedure(ws.id, t.doc);
      nav(`/procguard/procedure/${p.id}`);
    } catch (e) { onError((e as Error).message); }
  }

  const camName = (id: number) => cameras.find((c) => c.id === id)?.name ?? `cam ${id}`;

  return (
    <div className="panel" style={{ marginBottom: 14, ['--tone' as string]: ws.enabled ? 'var(--good)' : 'var(--text-dim)' }}>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong style={{ fontSize: 16 }}>{ws.name}</strong>
          <span className="muted" style={{ fontSize: 13, marginLeft: 8 }}>{[ws.site, ws.line].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="row" style={{ gap: 6, alignItems: 'center' }}>
          <ServiceStatusPill status={status} />
          <button className={`btn sm ${ws.enabled ? 'good' : ''}`} onClick={() => void toggleEnabled()}>
            {ws.enabled ? '● Activated' : '○ Activate'}
          </button>
          <button className="btn sm" onClick={() => nav(`/procguard/kiosk/${ws.id}`)}>Operator screen</button>
          <button className="btn sm" onClick={() => setShowAlarm((s) => !s)}>Alarms</button>
          <button className="btn sm" onClick={() => setShowModel((s) => !s)}>Model</button>
          <button className="btn sm bad" onClick={() => void removeWs()}>Delete</button>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        {editCams ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <CameraChooser cameras={cameras} value={camIds} onChange={setCamIds} customerId={ws.customerId} onError={onError}
              onCreated={(cid) => { setCamIds((v) => (v.includes(cid) ? v : [...v, cid])); void reloadCameras(); }} />
            <button className="btn sm primary" onClick={() => void saveCams()}>Save cameras</button>
            <button className="btn sm" onClick={() => { setEditCams(false); setCamIds(ws.cameras.map((c) => c.cameraId)); }}>Cancel</button>
          </div>
        ) : (
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="muted" style={{ fontSize: 12 }}>Cameras:</span>
            {ws.cameras.length === 0 ? <span className="muted" style={{ fontSize: 12 }}>none</span> : ws.cameras.map((c) => (
              <span key={c.cameraId} className="class-chip neutral" style={{ fontSize: 12 }}>{c.isPrimary ? '★ ' : ''}{camName(c.cameraId)}</span>
            ))}
            <button className="btn sm" onClick={() => setEditCams(true)}>Edit</button>
          </div>
        )}
      </div>

      {showAlarm && <AlarmEditor workstationId={ws.id} onError={onError} />}
      {showModel && <ModelPanel customerId={ws.customerId} workstationId={ws.id} onError={onError} />}

      <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <strong style={{ fontSize: 14 }}>SOPs</strong>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <select
              defaultValue=""
              title="Start a procedure from a ready-made template"
              onChange={(e) => { const v = e.target.value; e.target.value = ''; if (v) void fromTemplate(v); }}
              style={{ fontSize: 13 }}
            >
              <option value="" disabled>+ From template…</option>
              {PROCGUARD_TEMPLATES.map((t) => (
                <option key={t.key} value={t.key}>{t.category}: {t.name}</option>
              ))}
            </select>
            {newSop === null ? (
              <button className="btn sm primary" onClick={() => setNewSop('')}>+ New SOP</button>
            ) : (
              <span className="row" style={{ gap: 6 }}>
                <input
                  autoFocus
                  placeholder="SOP name"
                  value={newSop}
                  onChange={(e) => setNewSop(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newSop.trim()) void createSop(newSop);
                    if (e.key === 'Escape') setNewSop(null);
                  }}
                />
                <button className="btn sm primary" disabled={!newSop.trim()} onClick={() => void createSop(newSop)}>Create</button>
                <button className="btn sm" onClick={() => setNewSop(null)}>Cancel</button>
              </span>
            )}
          </div>
        </div>
        {procs.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>No procedures yet.</div>
        ) : (
          <table style={{ marginTop: 8, width: '100%' }}>
            <tbody>
              {procs.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/procguard/procedure/${p.id}`)}>
                  <td>{p.name}</td>
                  <td className="muted" style={{ fontSize: 12 }}>v{p.version}</td>
                  <td><span className={`badge ${p.status === 'published' ? 'good' : p.status === 'archived' ? 'unsure' : ''}`}>{p.status}</span></td>
                  <td className="muted" style={{ fontSize: 12, textAlign: 'right' }}>open →</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ModelPanel({ customerId, workstationId, onError }: { customerId: number; workstationId: number; onError: (m: string) => void }) {
  const [models, setModels] = useState<ProcguardModel[]>([]);
  const [name, setName] = useState('');
  const [classes, setClasses] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    procguardApi.listModels(customerId, workstationId).then(setModels).catch(() => undefined);
  }, [customerId, workstationId]);
  useEffect(() => load(), [load]);

  async function upload() {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('model', file);
      fd.append('customerId', String(customerId));
      fd.append('workstationId', String(workstationId));
      fd.append('name', name || file.name);
      fd.append('classes', classes);
      await procguardApi.uploadModel(fd);
      setFile(null); setName(''); setClasses(''); load();
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="panel" style={{ marginTop: 10, background: 'transparent' }}>
      <div className="muted" style={{ fontSize: 12 }}>
        Trained component model (YOLOX ONNX). Class names must be the part numbers it detects. With
        no active model, steps verify by presence; with one, they verify the exact part.
      </div>
      {models.map((m) => (
        <div key={m.id} className="row" style={{ gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`badge ${m.active ? 'good' : ''}`}>{m.active ? 'Active' : 'Inactive'}</span>
          <span>{m.name}</span>
          <span className="muted" style={{ fontSize: 12 }}>{m.classes.length} classes</span>
          {m.active
            ? <button className="btn sm" onClick={() => procguardApi.deactivateModel(m.id).then(load)}>Deactivate</button>
            : <button className="btn sm good" onClick={() => procguardApi.activateModel(m.id).then(load).catch((e) => onError(e.message))}>Activate</button>}
          <button className="btn sm bad" title="Delete model" onClick={() => { if (confirm(`Delete model "${m.name}"? This cannot be undone.`)) void procguardApi.deleteModel(m.id).then(load); }}>Delete</button>
        </div>
      ))}
      <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kit parts v1" /></div>
        <div style={{ flex: 1, minWidth: 200 }}><label>Classes (= part numbers, comma-sep)</label><input value={classes} onChange={(e) => setClasses(e.target.value)} placeholder="M6-20, NUT-M6, WASHER-6" style={{ width: '100%' }} /></div>
        {/* Custom control: the native file input renders the browser-locale
            label ("Bestand kiezen"), which broke the English-only UI. */}
        <label className="btn sm" style={{ cursor: 'pointer', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file?.name}>
          {file ? file.name : 'Choose .onnx file'}
          <input type="file" accept=".onnx" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
        <button className="btn sm primary" disabled={busy || !file} onClick={() => void upload()}>{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
    </div>
  );
}

function AlarmEditor({ workstationId, onError }: { workstationId: number; onError: (m: string) => void }) {
  const [targets, setTargets] = useState<AlarmTarget[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    procguardApi.getAlarm(workstationId).then((r) => { setTargets(r.targets); setLoaded(true); }).catch(() => setLoaded(true));
  }, [workstationId]);

  const save = () => procguardApi.setAlarm(workstationId, targets).catch((e) => onError((e as Error).message));
  const add = () => setTargets((t) => [...t, { name: '', url: '', method: 'GET', severities: ['critical', 'warning'] }]);
  const upd = (i: number, patch: Partial<AlarmTarget>) => setTargets((t) => t.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  if (!loaded) return null;
  return (
    <div className="panel" style={{ marginTop: 10, background: 'transparent' }}>
      <div className="muted" style={{ fontSize: 12 }}>Alarm targets fired on a deviation (andon lamp, siren, PLC/HTTP, chat webhook).</div>
      {targets.map((t, i) => (
        <div key={i} className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <input placeholder="name" value={t.name} onChange={(e) => upd(i, { name: e.target.value })} style={{ width: 110 }} />
          <input placeholder="https://…" value={t.url} onChange={(e) => upd(i, { url: e.target.value })} style={{ flex: 1, minWidth: 180 }} />
          <select value={t.method} onChange={(e) => upd(i, { method: e.target.value as 'GET' | 'POST' })}><option>GET</option><option>POST</option></select>
          <button className="btn sm bad" onClick={() => setTargets((ts) => ts.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button className="btn sm" onClick={add}>+ Target</button>
        <button className="btn sm primary" onClick={() => void save()}>Save alarms</button>
      </div>
    </div>
  );
}
